import React, { useState, useEffect, useRef } from 'react';
import { Database, RefreshCw, X, MessageSquare, Activity, TrendingUp, Loader2, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { apiFetch } from '../api';
import AnimatedBackground from '../components/Background';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';

export default function FCMQueueStats({ onBack, setError, API_BASE_URL, activeTab, loginId, deliveryMode }) {
  const { client } = useWebSocket();
  const [queueStatsLoading, setQueueStatsLoading] = useState(true);
  const [queueData, setQueueData] = useState(null);
  const [isLiveDataEnabled, setIsLiveDataEnabled] = useState(false); // Default OFF
  const [refreshInterval, setRefreshInterval] = useState(30); // Default 30 seconds
  const [showIntervalDropdown, setShowIntervalDropdown] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const isMounted = useRef(true);
  const dropdownRef = useRef(null);

  const intervalOptions = [
    { value: 15, label: '15 sec' },
    { value: 30, label: '30 sec' },
    { value: 45, label: '45 sec' },
    { value: 60, label: '1 min' },
    { value: 300, label: '5 min' },
    { value: 600, label: '10 min' },
    { value: 900, label: '15 min' }
  ];

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIntervalDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Countdown timer
  useEffect(() => {
    let countdownInterval;

    if (isLiveDataEnabled && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            return refreshInterval; // Reset when reaches 0
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [isLiveDataEnabled, countdown, refreshInterval]);

  const getQueueStatusText = () => {
    switch (activeTab) {
      case 'push':
        return (
          <div>
            <h1 className="text-xl font-bold text-white">FCM Queue Status Report</h1>
            <p className="text-sm text-white/60">Real-time Queue Monitoring</p>
          </div>
        );
      case 'email':
        return (
          <div>
            <h1 className="text-xl font-bold text-white">EMAIL Queue Status Report</h1>
            <p className="text-sm text-white/60">Real-time Email Queue Monitoring</p>
          </div>
        );
      case 'sms':
        return (
          <div>
            <h1 className="text-xl font-bold text-white">SMS Queue Status Report</h1>
            <p className="text-sm text-white/60">Real-time SMS Queue Monitoring</p>
          </div>
        );
      default:
        return (
          <div>
            <h1 className="text-xl font-bold text-white">ALL Queue Status Report</h1>
            <p className="text-sm text-white/60">View real-time statistics and insights</p>
          </div>
        );
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatCountdown = (seconds) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  const getSuggestionClass = (suggestion) => {
    if (!suggestion) return '';
    const upper = suggestion.toUpperCase();
    if (upper.includes('CRITICAL')) return 'critical';
    if (upper.includes('HIGH') || upper.includes('MONITOR') || upper.includes('MODERATE')) return 'warning';
    if (upper.includes('OK') || upper.includes('GOOD')) return 'ok';
    return '';
  };

  // --- MAIN EFFECT FOR DATA FETCHING & POLLING ---
  useEffect(() => {
    let intervalId;
    let subscription;

    // 1. Define Config based on Active Tab
    let config = {
      apiEndpoint: "/getQueueStatusMap",
      wsPubDestination: "/app/getQueueStatusMap",
      wsSubTopic: `/topic/getQueueStatusMap/${loginId}`
    };

    if (activeTab === 'email') {
      config = {
        apiEndpoint: "/getEmailQueueStatus",
        wsPubDestination: "/app/getEmailQueueStatus",
        wsSubTopic: `/topic/getEmailQueueStatus/${loginId}`
      };
    } else if (activeTab === 'push') {
      config = {
        apiEndpoint: "/getQueueStatusMap",
        wsPubDestination: "/app/getQueueStatusMap",
        wsSubTopic: `/topic/getQueueStatusMap/${loginId}`
      };
    } else if (activeTab === 'sms') {
      config = {
        apiEndpoint: "/getSmsQueueStatus",
        wsPubDestination: "/app/getSmsQueueStatus",
        wsSubTopic: `/topic/getSmsQueueStatus/${loginId}`
      };
    }

    const startMonitoring = async () => {
      setQueueStatsLoading(true);
      setError('');

      if (deliveryMode === 'socket') {
        // --- WEBSOCKET MODE ---
        try {
          await ensureSocketConnection(client);

          if (!isMounted.current) return;

          // A. Subscribe ONCE
          subscription = client.subscribe(config.wsSubTopic, (msg) => {
            if (!isMounted.current) return;
            try {
              const payload = JSON.parse(msg.body);
              const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;
              setQueueData(data);
              setQueueStatsLoading(false);
            } catch (e) {
              console.error('Failed to parse queue stats', e);
              setQueueStatsLoading(false);
            }
          });

          // B. Define Polling Function (Publish Request)
          const pollSocket = () => {
            if (client && client.connected) {
              client.publish({
                destination: config.wsPubDestination,
                body: JSON.stringify({})
              });
            }
          };

          // C. Initial Call
          pollSocket();

          // D. Setup interval only if live data is enabled
          if (isLiveDataEnabled) {
            setCountdown(refreshInterval); // Start countdown
            intervalId = setInterval(() => {
              pollSocket();
              setCountdown(refreshInterval); // Reset countdown
            }, refreshInterval * 1000);
          }

        } catch (connError) {
          console.error(connError);
          if (isMounted.current) {
            setError('Socket connection failed.');
            setQueueStatsLoading(false);
          }
        }

      } else {
        // --- API MODE ---
        const fetchApiData = async () => {
          try {
            const res = await apiFetch(config.apiEndpoint, { method: 'GET' });
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

            const data = await res.json();
            if (isMounted.current) {
              setQueueData(data);
              setQueueStatsLoading(false);
            }
          } catch (err) {
            console.error("API Fetch error:", err);
            if (isMounted.current) {
              if (!queueData) setError('Failed to fetch stats.');
              setQueueStatsLoading(false);
            }
          }
        };

        // Initial Call
        fetchApiData();

        // Setup interval only if live data is enabled
        if (isLiveDataEnabled) {
          setCountdown(refreshInterval); // Start countdown
          intervalId = setInterval(() => {
            fetchApiData();
            setCountdown(refreshInterval); // Reset countdown
          }, refreshInterval * 1000);
        }
      }
    };

    startMonitoring();

    // CLEANUP
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (subscription) subscription.unsubscribe();
    };
  }, [activeTab, deliveryMode, client, loginId, setError, isLiveDataEnabled, refreshInterval]);

  // Manual Refresh Button Handler
  const handleManualRefresh = async () => {
    setQueueStatsLoading(true);

    if (deliveryMode === 'socket' && client?.connected) {
      let dest = "/app/getQueueStatusMap";
      if (activeTab === 'email') dest = "/app/getEmailQueueStatus";
      else if (activeTab === 'sms') dest = "/app/getSmsQueueStatus";

      try {
        client.publish({ destination: dest, body: JSON.stringify({}) });
      } catch (error) {
        console.error('Failed to publish:', error);
        setQueueStatsLoading(false);
      }
    } else {
      // API mode
      let endpoint = "/getQueueStatusMap";
      if (activeTab === 'email') endpoint = "/getEmailQueueStatus";
      else if (activeTab === 'sms') endpoint = "/getSmsQueueStatus";

      try {
        const res = await apiFetch(endpoint, { method: 'GET' });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        setQueueData(data);
        setQueueStatsLoading(false);
      } catch (error) {
        console.error('Failed to fetch:', error);
        setQueueStatsLoading(false);
      }
    }
  };

  // Toggle Live Data
  const toggleLiveData = () => {
    const newState = !isLiveDataEnabled;
    setIsLiveDataEnabled(newState);

    if (newState) {
      setCountdown(refreshInterval); // Start countdown when enabled
    } else {
      setCountdown(0); // Clear countdown when disabled
    }
  };

  // Handle interval selection
  const handleIntervalChange = (value) => {
    setRefreshInterval(value);
    setShowIntervalDropdown(false);

    if (isLiveDataEnabled) {
      setCountdown(value); // Reset countdown with new interval
    }
  };

  if (!queueData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
        <span className="text-white ml-3 text-lg">Loading queue statistics...</span>
      </div>
    );
  }

  const overallStatsCards = [
    { title: 'Total Messages', value: queueData.totalMessages, icon: MessageSquare, color: 'from-blue-500 to-blue-600' },
    { title: 'Total Memory', value: formatBytes(queueData.estimatedTotalMemoryBytes), icon: Database, color: 'from-green-500 to-green-600' },
    { title: 'Heap Usage', value: `${queueData.heapUsagePercent}%`, icon: Activity, color: 'from-yellow-500 to-orange-500' },
    { title: 'Available Heap', value: formatBytes(queueData.availableHeapBytes), icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
  ];

  const messagesPerAppData = Object.entries(queueData.perAppQueueStatus).map(([appName, appData]) => ({
    name: appName,
    messages: appData.messageCount,
  }));

  const memoryPerAppData = Object.entries(queueData.perAppQueueStatus).map(([appName, appData]) => ({
    name: appName,
    memoryKB: appData.estimatedMemoryKB,
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF19A3'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] font-inter text-white">
      <AnimatedBackground />
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              {getQueueStatusText()}
            </div>
            <div className="flex items-center space-x-3">
              {/* Live Data Toggle - Only show in socket mode */}
              {deliveryMode === 'socket' && (
                <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-md border border-white/20 rounded-lg px-3 py-1.5">
                  <span className={`text-xs font-medium transition-colors duration-300 ${isLiveDataEnabled ? 'text-white' : 'text-white/50'}`}>
                    Live
                  </span>
                  <button
                    onClick={toggleLiveData}
                    className={`relative w-11 h-6 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${isLiveDataEnabled
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : 'bg-gray-600'
                      }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-lg transform transition-all duration-300 flex items-center justify-center ${isLiveDataEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isLiveDataEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                    </span>
                  </button>

                  {/* Countdown Timer */}
                  {isLiveDataEnabled && countdown > 0 && (
                    <div className="flex items-center space-x-1 bg-green-500/10 px-2 py-1 rounded">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-xs text-green-400 font-mono font-semibold min-w-[32px]">
                        {formatCountdown(countdown)}
                      </span>
                    </div>
                  )}

                  {/* Interval Dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowIntervalDropdown(!showIntervalDropdown)}
                      className="flex items-center space-x-1 px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 rounded text-xs transition-all duration-200"
                    >
                      <span>{intervalOptions.find(opt => opt.value === refreshInterval)?.label}</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>

                    {showIntervalDropdown && (
                      <div className="absolute right-0 mt-2 w-24 bg-slate-900 border border-white/20 rounded-lg shadow-xl overflow-hidden z-50">
                        {intervalOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleIntervalChange(option.value)}
                            className={`w-full px-3 py-2 text-left text-xs hover:bg-purple-500/20 transition-colors ${refreshInterval === option.value
                                ? 'bg-purple-500/30 text-purple-200'
                                : 'text-white/70'
                              }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={handleManualRefresh}
                disabled={queueStatsLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500/20 text-blue-200 rounded-lg hover:bg-blue-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${queueStatsLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button
                onClick={onBack}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-500/20 text-gray-200 rounded-lg hover:bg-gray-500/30 transition-all duration-200"
              >
                <X className="w-4 h-4" />
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {overallStatsCards.map((card) => (
            <div
              key={card.title}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">{card.title}</p>
                  <p className="text-2xl font-bold text-white">{card.value}</p>
                </div>
                <div className={`w-12 h-12 bg-gradient-to-r ${card.color} rounded-full flex items-center justify-center`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Overall Suggestion */}
        <div className="mb-8">
          <div className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center ${getSuggestionClass(queueData.overallSuggestion) === 'critical' ? 'animate-pulse' : ''
            }`}>
            <h2 className="text-xl font-bold text-white mb-4">Overall System Status</h2>
            <div className={`inline-block px-8 py-4 rounded-xl text-2xl font-bold ${getSuggestionClass(queueData.overallSuggestion) === 'ok' ? 'bg-green-500/20 text-green-200' :
              getSuggestionClass(queueData.overallSuggestion) === 'warning' ? 'bg-yellow-500/20 text-yellow-200' :
                'bg-red-500/20 text-red-200'
              }`}>
              {queueData.overallSuggestion}
            </div>
          </div>
        </div>

        {/* Per-App Queue Status */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Per-Application Queue Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(queueData.perAppQueueStatus).map(([appName, appData]) => (
              <div
                key={appName}
                className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center border-b border-white/20 pb-2">
                  {appName}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/60">Messages:</span>
                    <span className="text-white font-medium">{appData.messageCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Memory:</span>
                    <span className="text-white font-medium">{appData.estimatedMemoryKB} KB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Heap %:</span>
                    <span className="text-white font-medium">{appData.queueMemoryPercentOfHeap}%</span>
                  </div>
                  <div className={`mt-4 p-3 rounded-lg text-center font-bold ${getSuggestionClass(appData.suggestion) === 'ok' ? 'bg-green-500/20 text-green-200' :
                    getSuggestionClass(appData.suggestion) === 'warning' ? 'bg-yellow-500/20 text-yellow-200' :
                      'bg-red-500/20 text-red-200'
                    }`}>
                    {appData.suggestion}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Queue Performance Visualizations</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Messages Chart */}
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Messages Per Application</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={messagesPerAppData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <XAxis dataKey="name" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none' }} />
                    <Legend />
                    <Bar dataKey="messages" fill="#8884d8" name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Memory Chart */}
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Memory Usage (KB)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={memoryPerAppData}
                      dataKey="memoryKB"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#82ca9d"
                      label
                    >
                      {memoryPerAppData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Max Loaded App Info */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center">
          <h2 className="text-xl font-bold text-white mb-4">Max Loaded Application</h2>
          <p className="text-white/80 text-lg">
            The application with the largest queue size is: <span className="font-bold text-purple-400">{queueData.maxLoadedApp}</span>
            <br />
            (Containing {queueData.maxLoadedAppQueueSize} messages)
          </p>
        </div>

      </div>
    </div>
  );
}